import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWallet } from "../hooks/useWallet";
import { useApi } from "../hooks/useApi";
import NftRightsStack from "../components/NftRightsStack";
import { toImageSrc } from "../utils/ipfs";
import { ADDRESS_ENV, EXPECTED_CHAIN_ID, TOTAL_SUPPLY_CAP, envAddress } from "../utils/config";

interface NftRecord {
  tokenId: number;
  owner: string;
  metadataURI: string;
  metadataHash?: string;
  imageHash: string;
  imageURI?: string;
  active: boolean;
  mintedAt?: number;
  pendingWethFeesWei: string;
}

interface IndexerStats {
  totalSupplyCap: number;
  mintedCount: number;
  activeCount: number;
  burnedCount: number;
  remainingCount: number;
  pendingWethFeesWei: string;
}

export default function GalleryPage() {
  const { account } = useWallet();
  const { get } = useApi();
  const [nfts, setNfts] = useState<NftRecord[]>([]);
  const [stats, setStats] = useState<IndexerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [ownerFilter, setOwnerFilter] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load(owner?: string) {
    setLoading(true); setError(null);
    try {
      const query = owner ? `?owner=${encodeURIComponent(owner)}` : "";
      const [data, collectionStats] = await Promise.all([
        get<NftRecord[]>(`/indexer/nfts${query}`),
        get<IndexerStats>("/indexer/stats")
      ]);
      setNfts(data);
      setStats(collectionStats);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load The Colony display");
    } finally {
      setLoading(false);
    }
  }

  function handleFilter(e: FormEvent) {
    e.preventDefault();
    void load(ownerFilter || undefined);
  }

  function showOwner(owner: string) {
    setOwnerFilter(owner);
    void load(owner);
  }

  const loadedActiveCount = nfts.filter((nft) => nft.active).length;
  const loadedBurnedCount = nfts.length - loadedActiveCount;
  const loadedPendingWei = nfts.reduce((sum, nft) => sum + BigInt(nft.pendingWethFeesWei || "0"), 0n);
  const collectionPendingWei = BigInt(stats?.pendingWethFeesWei || "0");
  const activeCount = stats?.activeCount ?? loadedActiveCount;
  const burnedCount = stats?.burnedCount ?? loadedBurnedCount;
  const mintedCount = stats?.mintedCount ?? nfts.length;
  const supplyCap = stats?.totalSupplyCap ?? TOTAL_SUPPLY_CAP;
  const remainingCount = stats?.remainingCount ?? Math.max(0, supplyCap - mintedCount);
  const pendingSurfaceWei = ownerFilter ? loadedPendingWei : collectionPendingWei || loadedPendingWei;
  const topPending = useMemo(
    () => [...nfts]
      .sort((a, b) => compareWeiStrings(b.pendingWethFeesWei, a.pendingWethFeesWei))
      .slice(0, 3),
    [nfts]
  );

  return (
    <main className="page colony-page">
      <div className="container" style={{ paddingBottom: "var(--space-24)" }}>
        <section className="colony-hero" aria-labelledby="colony-title">
          <div className="colony-hero__copy">
            <div className="badge badge-prime">Live AI-signed display</div>
            <span className="eyebrow">// cryptographic fossils, indexed live</span>
            <h1 id="colony-title" className="colony-title">The Colony</h1>
            <p className="text-xl text-muted colony-hero__lede">
              Every AI-agent handoff Ape on chain. Each one carries Skill, image, and handoff evidence that makes its AI authorship verifiable later. This is the live ledger of those fossils: owner, queued WETH, burn state, and provenance from the read-only indexer.
            </p>
            <div className="colony-hero__actions">
              <Link to="/mint" className="btn btn-primary">Summon and Sign</Link>
              <Link to="/fees" className="btn btn-secondary">Claim WETH Fees</Link>
            </div>
          </div>

          <div className="colony-command" aria-label="Live colony feed">
            <div className="colony-command__bar"><span /> <span /> <span /></div>
            <div className="colony-command__line"><span className="text-muted">colony.status</span> --source /indexer/stats</div>
            <div className="colony-command__readout">
              <strong>{mintedCount.toLocaleString()}</strong>
              <span>summoned</span>
            </div>
            <div className="colony-command__grid">
              <span>{activeCount.toLocaleString()} active</span>
              <span>{burnedCount.toLocaleString()} burned</span>
              <span>{remainingCount.toLocaleString()} remaining</span>
              <span>{formatWeth(pendingSurfaceWei)} pending</span>
            </div>
          </div>
        </section>

        <section className="colony-stats" aria-label="Live colony stats">
          <ColonyMetric label="Minted" value={mintedCount.toLocaleString()} detail={`${supplyCap.toLocaleString()} max supply`} />
          <ColonyMetric label="Active" value={activeCount.toLocaleString()} detail="earning future WETH" tone="green" />
          <ColonyMetric label="Burned" value={burnedCount.toLocaleString()} detail="converted to CAPE" tone="red" />
          <ColonyMetric label="Remaining" value={remainingCount.toLocaleString()} detail="still summonable" tone="green" />
          <ColonyMetric label={ownerFilter ? "Wallet pending WETH" : "Pending WETH"} value={formatWeth(pendingSurfaceWei)} detail={ownerFilter ? "loaded wallet surface" : "collection fee surface"} tone="weth" />
        </section>

        <section className="colony-panel" aria-labelledby="colony-filter-title">
          <div>
            <div className="badge badge-cyan" style={{ marginBottom: "var(--space-3)" }}>Read-only owner lens</div>
            <h2 id="colony-filter-title" className="text-2xl">Watch a wallet or scan the full colony</h2>
            <p className="text-muted" style={{ marginTop: "var(--space-2)" }}>
              Filters keep the same read-only indexer calls. No generation, pinning, voucher signing, or contract writes happen on this page.
            </p>
          </div>
          <form onSubmit={handleFilter} className="colony-filter">
            <input
              id="gallery-owner-filter"
              className="input text-mono"
              placeholder="Filter by wallet address…"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary" id="gallery-filter-btn">Filter</button>
            {account && <button type="button" className="btn btn-secondary" onClick={() => showOwner(account)} id="gallery-my-apes-btn">My Apes</button>}
            <button type="button" className="btn btn-secondary" onClick={() => { setOwnerFilter(""); void load(); }} id="gallery-clear-btn">All</button>
          </form>
        </section>

        <div className="alert alert-warning colony-warning">
          <span>⚠️</span>
          <div>
            <strong>Marketplace fee warning:</strong> pending WETH belongs to the current owner until claimed. A seller can claim pending fees before a third-party marketplace purchase settles, so pending fees are not guaranteed in off-site buys.
          </div>
        </div>

        <NftRightsStack variant="compact" showHeader className="colony-rights-summary" />

        {error && <div className="alert alert-error" style={{ marginBottom: "var(--space-6)" }}>{error}</div>}

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-24)" }}><span className="spinner spinner-lg" /></div>
        ) : nfts.length === 0 ? (
          <EmptyState ownerFilter={ownerFilter} />
        ) : (
          <>
            <section className="colony-panel colony-panel--split" aria-label="Loaded colony summary">
              <div>
                <div className="badge badge-gold" style={{ marginBottom: "var(--space-3)" }}>{ownerFilter ? "Wallet view" : "Live loaded set"}</div>
                <h2 className="text-2xl">{nfts.length.toLocaleString()} Apes loaded</h2>
                <p className="text-muted" style={{ marginTop: "var(--space-2)" }}>
                  {loadedActiveCount.toLocaleString()} active, {loadedBurnedCount.toLocaleString()} burned, {formatWeth(loadedPendingWei)} pending WETH in this view.
                </p>
              </div>
              <div className="colony-top-fees">
                <div className="text-mono text-xs text-muted">Top pending WETH in this view</div>
                {topPending.map((nft) => (
                  <Link key={nft.tokenId} to={`/nft/${nft.tokenId}`} className="colony-top-fees__row">
                    <span>#{nft.tokenId}</span>
                    <strong>{formatWeth(BigInt(nft.pendingWethFeesWei || "0"))}</strong>
                  </Link>
                ))}
              </div>
            </section>

            <div className="nft-ledger colony-ledger" style={{ marginBottom: "var(--space-6)" }}>
              <div className="nft-ledger__head colony-ledger__grid">
                <span>ID</span><span>Status</span><span>Owner</span><span>Pending WETH</span><span>Age</span><span>Metadata</span>
              </div>
              {nfts.slice(0, 24).map((nft) => <LedgerRow key={nft.tokenId} nft={nft} />)}
            </div>

            <div className="colony-grid">
              {nfts.map((nft) => <NftCard key={nft.tokenId} nft={nft} onOwnerSelect={showOwner} />)}
            </div>
          </>
        )}
      </div>
    </main>
  );
}

function ColonyMetric({ label, value, detail, tone }: { label: string; value: string; detail: string; tone?: "green" | "red" | "weth" }) {
  return (
    <div className={`colony-stat ${tone ? `colony-stat--${tone}` : ""}`}>
      <div className="colony-stat__value">{value}</div>
      <div className="colony-stat__label">{label}</div>
      <div className="colony-stat__detail">{detail}</div>
    </div>
  );
}

function LedgerRow({ nft }: { nft: NftRecord }) {
  return (
    <Link to={`/nft/${nft.tokenId}`} className="nft-ledger__row colony-ledger__grid">
      <span className="text-mono">#{nft.tokenId}</span>
      <span><span className={`badge ${nft.active ? "badge-green" : "badge-red"}`}>{nft.active ? "Active" : "Burned"}</span></span>
      <span className="text-mono">{shorten(nft.owner)}</span>
      <span className="text-mono" style={{ color: "var(--color-weth)" }}>{formatWeth(BigInt(nft.pendingWethFeesWei || "0"))}</span>
      <span className="text-mono">{activeAgeLabel(nft)}</span>
      <span className="text-mono">{shorten(nft.metadataHash ?? nft.metadataURI)}</span>
    </Link>
  );
}

function NftCard({ nft, onOwnerSelect }: { nft: NftRecord; onOwnerSelect: (owner: string) => void }) {
  const tokenUrl = etherscanTokenUrl(nft.tokenId);
  const ownerUrl = `${etherscanBaseUrl()}/address/${nft.owner}`;

  return (
    <article className="colony-card" id={`nft-card-${nft.tokenId}`}>
      <Link to={`/nft/${nft.tokenId}`} className="colony-card__image-link" aria-label={`View CODE APE #${nft.tokenId}`}>
        {nft.imageURI ? (
          <img className="colony-card__image" src={toImageSrc(nft.imageURI)} alt={`CODE APE #${nft.tokenId}`} />
        ) : (
          <div className="colony-card__image-placeholder"><span>🦍</span></div>
        )}
      </Link>

      <div className="colony-card__menu" aria-label={`Actions for CODE APE #${nft.tokenId}`}>
        <Link to={`/nft/${nft.tokenId}`}>View detail</Link>
        {tokenUrl ? <a href={tokenUrl} target="_blank" rel="noreferrer">View on Etherscan</a> : <Link to={`/nft/${nft.tokenId}`}>View provenance</Link>}
        <button type="button" onClick={() => onOwnerSelect(nft.owner)}>View owner</button>
      </div>

      <div className="colony-card__body">
        <div className="colony-card__topline">
          <span className="text-mono">#{nft.tokenId}</span>
          <span className={`badge ${nft.active ? "badge-green" : "badge-red"}`}>{nft.active ? "Active" : "Burned"}</span>
        </div>
        <h3>Ape #{nft.tokenId}</h3>
        <div className="colony-card__facts">
          <span>Owner <strong className="text-mono">{shorten(nft.owner)}</strong></span>
          <span>Pending <strong>{formatWeth(BigInt(nft.pendingWethFeesWei || "0"))}</strong></span>
          <span>Age <strong>{activeAgeLabel(nft)}</strong></span>
        </div>
        <div className="colony-card__links">
          <a href={ownerUrl} target="_blank" rel="noreferrer">Owner ↗</a>
          <Link to={`/nft/${nft.tokenId}`}>Detail →</Link>
        </div>
      </div>
    </article>
  );
}

function EmptyState({ ownerFilter }: { ownerFilter: string }) {
  return (
    <div className="card text-center" style={{ padding: "var(--space-24)" }}>
      <div style={{ fontSize: "4rem", marginBottom: "var(--space-4)" }}>🦍</div>
      <p className="text-xl text-muted">No Apes indexed yet.</p>
      {ownerFilter && <p className="text-muted text-sm" style={{ marginTop: "var(--space-2)" }}>No Apes owned by this address.</p>}
      <Link to="/mint" className="btn btn-secondary" style={{ marginTop: "var(--space-6)" }}>Sign a summoned Ape</Link>
    </div>
  );
}

function shorten(value: string): string {
  if (!value) return "—";
  return value.length > 18 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;
}

function formatWeth(wei: bigint): string {
  const scaled = wei * 1000000n / 10n ** 18n;
  return `${(Number(scaled) / 1000000).toFixed(6)} WETH`;
}

function activeAgeLabel(nft: NftRecord): string {
  if (!nft.active) return "Burned";
  if (!nft.mintedAt) return "Unknown";
  if (nft.mintedAt < 946684800) return `Block ${nft.mintedAt.toLocaleString()}`;
  const elapsedMs = Date.now() - nft.mintedAt * 1000;
  const days = Math.max(0, Math.floor(elapsedMs / 86400000));
  return days === 1 ? "1 day" : `${days.toLocaleString()} days`;
}

function compareWeiStrings(a: string, b: string): number {
  const left = BigInt(a || "0");
  const right = BigInt(b || "0");
  return left === right ? 0 : left > right ? 1 : -1;
}

function etherscanBaseUrl(): string {
  if (EXPECTED_CHAIN_ID === 8453) return "https://basescan.org";
  if (EXPECTED_CHAIN_ID === 84532) return "https://sepolia.basescan.org";
  return EXPECTED_CHAIN_ID === 11155111 ? "https://sepolia.etherscan.io" : "https://etherscan.io";
}

function etherscanTokenUrl(tokenId: number): string | null {
  const nftAddress = envAddress(ADDRESS_ENV.nft);
  if (!nftAddress) return null;
  return `${etherscanBaseUrl()}/nft/${nftAddress}/${tokenId}`;
}
